import React from "react";
import FetchHelper from "./lib/fetchHelper";
import { BPSwapOfferStatus } from "./lib/bpSwapOfferStatus";
import { ShiftLine, SWAP_CHANGED_EVENT, shiftInSentence } from "./lib/swapShiftLine";
import ShiftPeek from "./lib/shiftPeek";
import { BPMemberRole } from "./lib/bpMemberRole";
import ConfirmDialog from "./lib/confirmDialog";
import DateUtility from "./lib/dateUtility";
/**
 * How many of the volunteer's own shifts the pick list shows at a time. A volunteer who takes a lot
 * of shifts has a long list, and the whole of it at once is unreadable - especially on the phone,
 * which is where most of them answer a byttevagt.
 */
const PAGE_SIZE = 5;
/** The outlined circles of the pick lists, as the boards draw them. */
const CircleIcon = ({ kind }) => (React.createElement("svg", { className: kind === "plus" ? "bi-bp-plus" : kind === "minus" ? "bi-bp-minus" : undefined, width: kind === "info" ? 22 : 24, height: kind === "info" ? 22 : 24, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: kind === "info" ? 1.75 : 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true" },
    React.createElement("circle", { cx: "12", cy: "12", r: "9" }),
    React.createElement("path", { d: kind === "plus" ? "M12 8v8M8 12h8" : kind === "minus" ? "M8 12h8" : "M12 11v5M12 8h.01" })));
const sameSet = (a, b) => a.length === b.length && a.every((id) => b.includes(id));
/**
 * "Byt med mig" - the volunteer's one offer on somebody else's byttevagt: pick one or more of your
 * own upcoming shifts, optionally say something, and send.
 *
 * It is an editor rather than a one-shot form. A volunteer has at most one pending offer per
 * byttevagt, so when one already exists the form opens with its shifts already chosen and its
 * message in the box - exactly as if they had just picked them - and saving makes the offer name
 * whatever is chosen at that moment. There is no separate list of "shifts already sent": they are
 * simply chosen.
 *
 * Offering several shifts is normal rather than an edge case; the owner picks exactly one when they
 * accept, so naming more is simply a better chance of one of them suiting.
 */
export default class SwapOfferProposeComponent extends React.Component {
    constructor(props) {
        super(props);
        /**
         * Seeds the chosen list and the message from the standing offer every time, so after a save,
         * a withdrawal or a failure the form shows what is actually on the table.
         */
        this.load = () => FetchHelper.get(`/bpapi/shiftswap/candidates/${this.props.slug}`)
            .then((result) => {
            var _a, _b, _c;
            // One pending offer per volunteer per byttevagt - the database enforces it.
            const offer = (_a = result.myPendingOffers[0]) !== null && _a !== void 0 ? _a : null;
            const saved = offer ? offer.candidates.map((c) => c.shiftMemberId) : [];
            const message = (_b = offer === null || offer === void 0 ? void 0 : offer.message) !== null && _b !== void 0 ? _b : "";
            this.setState({
                isLoaded: true,
                busy: false,
                exchangeId: (_c = result.shiftExchange) === null || _c === void 0 ? void 0 : _c.id,
                candidates: result.candidates,
                offer,
                maxPendingOffers: result.maxPendingOffers,
                offeredToYou: result.offeredToYou,
                chosen: saved,
                savedChosen: saved,
                message,
                savedMessage: message
            });
        })
            .catch(() => this.setState({ isLoaded: true, busy: false, error: "Dine vagter kunne ikke hentes" }));
        /**
         * A withdrawal only fails because the offer stopped being pending, and much the likeliest
         * reason is that the owner accepted it while this page sat open. That is not a failure the
         * volunteer should retry - the rosters have already changed hands - so it is worth one extra
         * request to say what actually happened instead of "could not be withdrawn".
         */
        this.explainWithdrawFailure = (fallback) => FetchHelper.get(`/bpapi/shiftswap/mine?includeResolved=true`)
            .then((result) => {
            const accepted = result.items.find((offer) => {
                var _a;
                return ((_a = offer.shiftExchange) === null || _a === void 0 ? void 0 : _a.id) === this.state.exchangeId
                    && offer.status === BPSwapOfferStatus.accepted;
            });
            this.setState({
                busy: false,
                // Clear the "sent" banner: leaving a green success notice above a red failure
                // reads as two contradictory answers to the same action.
                sent: null,
                error: accepted
                    ? `${this.props.ownername} har allerede accepteret et af dine bytteforslag. Byttet er gennemført og kan ikke fortrydes.`
                    : fallback
            });
        })
            .catch(() => this.setState({ busy: false, sent: null, error: fallback }))
            .then(() => this.load());
        /**
         * Moves a shift between the two lists. Picking one takes it out of the paged list and into
         * "Du har valgt disse vagter" above it, and removing it puts it back - a shift is only ever in
         * one of the two, so there is nothing to tick and nothing to lose sight of.
         */
        this.choose = (shiftMemberId) => this.setState({ chosen: [...this.state.chosen, shiftMemberId] });
        this.unchoose = (shiftMemberId) => this.setState({
            chosen: this.state.chosen.filter((id) => id !== shiftMemberId)
        });
        /**
         * Sends the whole chosen set; the server makes the offer name exactly that, creating it if
         * there was none.
         *
         * Raw fetch rather than FetchHelper: the server answers a refused offer with a 400 and the reason
         * as plain text - the cap, a shift that collides, a role the owner does not hold, the
         * reciprocal-offer refusal - and FetchHelper's reject discards the body in favour of the status
         * line. Those messages are the whole point of the validation.
         */
        this.submit = () => {
            const wasEdit = this.state.offer !== null;
            this.setState({ busy: true, error: null, sent: null });
            fetch(`/bpapi/shiftswap`, FetchHelper.postOptions({
                slug: this.props.slug,
                shiftMemberIds: this.state.chosen,
                message: this.state.message
            }))
                .then((response) => response.ok
                ? response.json().then(() => {
                    this.setState({ sent: wasEdit ? "updated" : "created" });
                    // Reload here rather than in a following then(): setState does not flush
                    // synchronously, so anything reading this.state afterwards would still see the
                    // offer as it was before the save.
                    this.load();
                })
                : response.text().then((body) => this.setState({
                    busy: false,
                    error: body || "Dit bytteforslag kunne ikke gemmes"
                })))
                .catch(() => this.setState({ busy: false, error: "Dit bytteforslag kunne ikke gemmes" }));
        };
        /**
         * "Byt nu": accepts the owner's standing offer from this end. They offered this very shift on
         * one of the viewer's own byttevagter, so the viewer is the one entitled to accept it - the same
         * accept Mine byttevagter makes, with this byttevagt's shift as the choice.
         *
         * Raw fetch for the same reason as submit: a refusal's reason comes back in the body. A refusal
         * reloads, since the likeliest cause is that the offer has gone and the notice with it.
         */
        this.swap = () => {
            const { offerId, shiftMemberId } = this.state.offeredToYou;
            const failure = "Byttet kunne ikke gennemføres";
            this.setState({ busy: true, error: null, sent: null });
            fetch(`/bpapi/shiftswap/${offerId}/accept`, FetchHelper.postOptions({ shiftMemberId }))
                .then((response) => response.ok
                ? response.json().then(() => {
                    var _a, _b;
                    window.dispatchEvent(new CustomEvent(SWAP_CHANGED_EVENT));
                    (_b = (_a = this.props).onSwapped) === null || _b === void 0 ? void 0 : _b.call(_a, this.state.offeredToYou);
                })
                : response.text().then((body) => {
                    this.setState({ confirmingSwap: false, error: body || failure });
                    this.load();
                }))
                .catch(() => this.setState({ busy: false, confirmingSwap: false, error: failure }));
        };
        /**
         * Takes back the volunteer's offer on this byttevagt, for Fortryd - abandoning the idea of
         * swapping altogether.
         *
         * Resolves true when there is nothing left outstanding, so the caller only closes the form if
         * the withdrawal actually went through; a failure leaves the form open with its error showing
         * rather than hiding an offer that is still live.
         */
        this.withdrawAll = () => {
            const offer = this.state.offer;
            if (!offer)
                return Promise.resolve(true);
            this.setState({ busy: true, error: null });
            return FetchHelper.del(`/bpapi/shiftswap/${offer.id}`)
                .then(() => this.load().then(() => true))
                .catch(() => this
                .explainWithdrawFailure("Dit bytteforslag kunne ikke trækkes tilbage")
                .then(() => false));
        };
        /**
         * One shift, in whichever of the two lists it currently belongs to. The whole row is the
         * button - most volunteers do this on a phone - so the shift stamp is not a link here; who else
         * is on the shift and what is showing comes up in an overlay from the info icon at the end,
         * over this page rather than away from it.
         */
        this.renderCandidate = (candidate, isChosen) => {
            const start = DateUtility.parse(candidate.shift.dtStart);
            const end = DateUtility.parse(candidate.shift.dtEnd);
            const label = isChosen ? "Fjern fra dit bytteforslag" : "Tilføj til dit bytteforslag";
            return (React.createElement("div", { className: "bi-bp-row", key: `cand-${candidate.shiftMemberId}` },
                React.createElement("button", { type: "button", className: "bi-bp-pick", title: label, onClick: () => isChosen
                        ? this.unchoose(candidate.shiftMemberId)
                        : this.choose(candidate.shiftMemberId) },
                    React.createElement(CircleIcon, { kind: isChosen ? "minus" : "plus" }),
                    React.createElement("span", { className: "bi-bp-row-text" },
                        React.createElement("span", { className: "bi-bp-row-when" },
                            DateUtility.rowDay(start),
                            " \u00B7 ",
                            DateUtility.rowInterval(start, end)),
                        React.createElement("span", { className: "bi-bp-row-role" }, candidate.role === BPMemberRole.operator ? "Operatør" : "Kiosk")),
                    React.createElement("span", { className: "visually-hidden" }, label)),
                React.createElement("button", { type: "button", className: "bi-bp-info", "aria-label": "Om vagten", title: "Om vagten", onClick: () => this.setState({ peekShiftId: candidate.shift.id }) },
                    React.createElement(CircleIcon, { kind: "info" }))));
        };
        this.state = {
            isLoaded: false,
            candidates: [],
            // The volunteer's standing offer on this byttevagt, if they have one.
            offer: null,
            maxPendingOffers: 0,
            chosen: [],
            message: "",
            // What the offer currently names and says, so the save button can tell an edit from
            // no edit - and the owner is not sent an SMS for a save that changed nothing.
            savedChosen: [],
            savedMessage: "",
            page: 0,
            busy: false,
            error: null,
            sent: null
        };
    }
    componentDidMount() {
        var _a, _b;
        // A freshly opened form has nothing loaded yet, so it starts out saying so - the page may
        // still be holding the answer from the last time the form was open.
        (_b = (_a = this.props).onOfferStanding) === null || _b === void 0 ? void 0 : _b.call(_a, false);
        this.load();
    }
    /**
     * Tells the page whether an offer is standing, which decides what its cancel button is: taking
     * that offer back, or just closing the form. Reported on the change rather than on every load,
     * since every save and withdrawal reloads.
     */
    componentDidUpdate(_prevProps, prevState) {
        var _a, _b;
        if ((prevState.offer === null) !== (this.state.offer === null)) {
            (_b = (_a = this.props).onOfferStanding) === null || _b === void 0 ? void 0 : _b.call(_a, this.state.offer !== null);
        }
    }
    render() {
        var _a, _b;
        if (!this.state.isLoaded) {
            return (React.createElement("div", { className: "bp-loading" },
                React.createElement("i", { className: "fa fa-spinner fa-pulse fa-fw" })));
        }
        const isEdit = this.state.offer !== null;
        const offeredToYou = this.state.offeredToYou;
        // The offer's own shifts first, then the eligible ones over them. A shift offered earlier
        // can have stopped being eligible since - the owner may have taken another shift at that
        // time - and would then be missing from the candidates; it still has to show as chosen,
        // or it would vanish from the offer without the volunteer ever seeing it go.
        const byId = new Map();
        ((_b = (_a = this.state.offer) === null || _a === void 0 ? void 0 : _a.candidates) !== null && _b !== void 0 ? _b : []).forEach((c) => byId.set(c.shiftMemberId, c));
        this.state.candidates.forEach((c) => byId.set(c.shiftMemberId, c));
        const chosen = this.state.chosen.map((id) => byId.get(id)).filter(Boolean);
        // The cap is counted in shifts, over the one offer as a whole.
        const limit = this.state.maxPendingOffers;
        const remaining = Math.max(0, limit - this.state.chosen.length);
        const dirty = !sameSet(this.state.chosen, this.state.savedChosen)
            || this.state.message !== this.state.savedMessage;
        // Chosen shifts leave the pick list for the overview above it, so the paging shifts under
        // them as they are picked.
        const offerable = this.state.candidates.filter((c) => !this.state.chosen.includes(c.shiftMemberId));
        // Clamped rather than held in state: picking a shift takes it out of the list, and the page
        // the volunteer was on can cease to exist between one render and the next.
        const pageCount = Math.max(1, Math.ceil(offerable.length / PAGE_SIZE));
        const page = Math.max(0, Math.min(this.state.page, pageCount - 1));
        const first = page * PAGE_SIZE;
        const shown = offerable.slice(first, first + PAGE_SIZE);
        const nothingToOffer = this.state.candidates.length === 0 && chosen.length === 0;
        // Disabled on an unchanged edit: saving would change nothing, and an edit that adds a shift
        // sends the owner an SMS. Emptying the list is not a save either - Fortryd is how an offer is
        // taken back as a whole.
        const sendDisabled = this.state.busy || this.state.chosen.length === 0 || (isEdit && !dirty);
        return (React.createElement("div", { className: "bi-bp" },
            React.createElement("div", { className: "bi-bp-cols" },
                React.createElement("div", { className: "bi-bp-col" },
                    this.props.header,
                    offeredToYou
                        ? React.createElement("div", { className: "bi-bv-alert bi-bp-offered" },
                            React.createElement("p", null,
                                this.props.ownername,
                                " har allerede tilbudt dig et bytte med denne vagt",
                                offeredToYou.yourShift
                                    ? React.createElement(React.Fragment, null,
                                        " for din byttevagt ",
                                        React.createElement(ShiftLine, { shift: offeredToYou.yourShift, role: offeredToYou.yourRole }))
                                    : null,
                                "."),
                            React.createElement("div", { className: "bi-bp-offered-actions" },
                                React.createElement("button", { type: "button", className: "bi-bv-btn bi-bv-btn-success", disabled: this.state.busy, onClick: () => this.setState({ confirmingSwap: true }) }, "Byt nu"),
                                React.createElement("a", { href: `/min-side/mine-byttevagter/?slug=${offeredToYou.slug}` }, "Se forslaget under Mine byttevagter")))
                        : null,
                    this.state.confirmingSwap
                        ? React.createElement(ConfirmDialog, { heading: "Byt vagter", message: (offeredToYou === null || offeredToYou === void 0 ? void 0 : offeredToYou.yourShift)
                                ? `Du bytter din vagt ${shiftInSentence(offeredToYou.yourShift)} med denne vagt. Byttet kan ikke fortrydes. Er du sikker?`
                                : "Du bytter din vagt med denne vagt. Byttet kan ikke fortrydes. Er du sikker?", yesLabel: "Ja, byt", positive: true, busy: this.state.busy, onYes: this.swap, onNo: () => this.setState({ confirmingSwap: false }) })
                        : null,
                    this.state.peekShiftId
                        ? React.createElement(ShiftPeek, { key: this.state.peekShiftId, shiftId: this.state.peekShiftId, onClose: () => this.setState({ peekShiftId: null }) })
                        : null,
                    this.state.error ? React.createElement("div", { className: "bi-bv-alert bi-bv-alert-error", role: "alert" }, this.state.error) : null,
                    this.state.sent === "created"
                        ? React.createElement("div", { className: "bi-bv-alert bi-bv-alert-ok", role: "status" }, "Dit bytteforslag er sendt. Du f\u00E5r besked, n\u00E5r der er svar.")
                        : this.state.sent === "updated"
                            ? React.createElement("div", { className: "bi-bv-alert bi-bv-alert-ok", role: "status" }, "Dit bytteforslag er opdateret.")
                            : null,
                    chosen.length > 0
                        ? React.createElement("section", { className: "bi-bp-section" },
                            React.createElement("h2", null, "Du har valgt disse vagter:"),
                            React.createElement("div", { className: "bi-bp-list bi-bp-chosen" }, chosen.map((candidate) => this.renderCandidate(candidate, true))))
                        : null),
                React.createElement("section", { className: "bi-bp-col bi-bp-section" }, nothingToOffer
                    ? React.createElement("p", { className: "bi-bp-note" },
                        "Du har ingen kommende vagter, der kan byttes med denne. Det kan v\u00E6re, at du ikke har flere vagter, eller at ",
                        this.props.ownername,
                        " ikke har den rolle, dine vagter kr\u00E6ver.")
                    : remaining === 0
                        ? React.createElement("p", { className: "bi-bp-note" },
                            "Du kan tilbyde ",
                            limit,
                            " ",
                            limit === 1 ? "vagt" : "vagter",
                            " ad gangen.",
                            limit === 1
                                ? " Fjern den, hvis du hellere vil tilbyde en anden."
                                : " Fjern en af dem, hvis du hellere vil tilbyde en anden.")
                        : offerable.length === 0
                            ? React.createElement("p", { className: "bi-bp-note" }, "Du har ikke flere vagter at v\u00E6lge imellem.")
                            : React.createElement(React.Fragment, null,
                                React.createElement("h2", null, chosen.length > 0
                                    ? `Du kan tilbyde ${remaining} ${remaining === 1 ? "vagt" : "vagter"} mere:`
                                    : "Vælg den eller de vagter, du kan tilbyde i bytte:"),
                                React.createElement("div", { className: "bi-bp-list" }, shown.map((candidate) => this.renderCandidate(candidate, false))),
                                pageCount > 1
                                    ? React.createElement("div", { className: "bi-bp-pager" },
                                        React.createElement("button", { type: "button", disabled: page === 0, "aria-label": "Forrige vagter", onClick: () => this.setState({ page: page - 1 }) },
                                            React.createElement("i", { className: "fa-solid fa-chevron-left", "aria-hidden": "true" })),
                                        React.createElement("span", null,
                                            page + 1,
                                            "/",
                                            pageCount),
                                        React.createElement("button", { type: "button", disabled: page === pageCount - 1, "aria-label": "Flere vagter", onClick: () => this.setState({ page: page + 1 }) },
                                            React.createElement("i", { className: "fa-solid fa-chevron-right", "aria-hidden": "true" })))
                                    : null))),
            nothingToOffer
                ? null
                : React.createElement("div", { className: "bi-bp-message" },
                    React.createElement("label", { htmlFor: "swap-message" }, "Besked (valgfri)"),
                    React.createElement("textarea", { id: "swap-message", rows: 2, maxLength: 500, value: this.state.message, onChange: (e) => this.setState({ message: e.target.value }) })),
            React.createElement("div", { className: "bi-bp-actions" },
                React.createElement("button", { type: "button", className: "bi-bp-btn bi-bp-btn-outline", title: this.state.offer
                        ? "Lukker og trækker dine bytteforslag på denne vagt tilbage"
                        : undefined, onClick: () => { var _a, _b; return (_b = (_a = this.props).onCancel) === null || _b === void 0 ? void 0 : _b.call(_a); } }, "Fortryd"),
                nothingToOffer
                    ? null
                    : React.createElement("button", { type: "button", className: "bi-bp-btn bi-bp-btn-primary", disabled: sendDisabled, onClick: this.submit }, isEdit ? "Gem ændringer" : "Send bytteforslag"))));
    }
}
